import { useQuery } from '@apollo/client';
import { graphql } from '@client/graphql/types';
import { useMemo } from 'react';
import { DateRange, TrendsAnalyticsService } from '../../common/reservation-analytic-service';

const QUERY = graphql(`
  query analytic_reservation_getOverviewMetrics($branchId: ID!, $startTime: DateTime!, $endTime: DateTime!) {
    analytic_reservation_getOverviewMetrics(branchId: $branchId, startTime: $startTime, endTime: $endTime) {
      totalReservations
      totalGuests
      showUpRate
      cancelledReservations
    }
  }
`);

export default function useGetReservationOverViewMetric(branchId: string, dateRange: DateRange) {
  const previousDateRange = useMemo(() => TrendsAnalyticsService.getPreviousDateRange(dateRange), [dateRange]);

  // Current period query
  const {
    data: currentData,
    refetch: currentRefetch,
    loading: currentLoading,
    error: currentError,
  } = useQuery(QUERY, {
    variables: { branchId, startTime: dateRange.startDate, endTime: dateRange.endDate },
  });

  // Previous period query
  const {
    data: previousData,
    refetch: previousRefetch,
    loading: previousLoading,
    error: previousError,
  } = useQuery(QUERY, {
    variables: {
      branchId,
      startTime: previousDateRange.startDate,
      endTime: previousDateRange.endDate,
    },
  });

  // Computed values
  const analytics = useMemo(() => {
    if (
      !currentData?.analytic_reservation_getOverviewMetrics ||
      !previousData?.analytic_reservation_getOverviewMetrics
    ) {
      return null;
    }

    const current = currentData.analytic_reservation_getOverviewMetrics;
    const previous = previousData.analytic_reservation_getOverviewMetrics;
    const trends = {
      totalReservations: TrendsAnalyticsService.calculateTrend(current.totalReservations, previous.totalReservations),
      totalGuests: TrendsAnalyticsService.calculateTrend(current.totalGuests, previous.totalGuests),
      showUpRate: TrendsAnalyticsService.calculateTrend(current.showUpRate, previous.showUpRate),
      cancellationRate: TrendsAnalyticsService.calculateTrend(
        current.cancelledReservations,
        previous.cancelledReservations,
      ),
    };

    return {
      current,
      previous,
      trends,
    };
  }, [currentData, previousData]);

  const isLoading = currentLoading || previousLoading;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const error = currentError || previousError;

  return {
    analytics,
    isLoading,
    error,
    refetch: () => {
      // Refetch both queries
      return Promise.all([currentRefetch(), previousRefetch()]);
    },
  };
}
